import { Link, useParams, useNavigate } from "react-router-dom";
import { FileText, SquarePen, ChevronLeft, MoreVertical } from "lucide-react";
import { useState, useEffect, useContext, useRef } from "react";
import AuthContext from "../../store/auth-context";
import { GET, POST, PUT, DEL } from "../../service/CRUDService";
import Toast from "../../common/Toast/Toast";
import CfLoader from "../../common/CfLoader/CfLoader";
import EntityConstants from "../../configs/EntityConstants";
import PlusCircle from "../../resources/icons/plus-circle.svg";
import CreateBatchModal from "../../common/CreateBatchModal/CreateBatchModal";
import useNavigationStore from "../../store/navigationStore";
import DraftsRevokeModal from "../../common/DraftsRevokeModal/DraftsRevokeModal";
import useSearchDebounce from "../../hooks/useSearchDebounce";

export default function BatchPage() {
  const authContext = useContext(AuthContext);
  const { programmeId } = useParams();
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const {
    setBatch: setBatchInStore,
    currentProgramme,
    setProgramme,
  } = useNavigationStore();

  const [searchTerm, setSearchTerm] = useState("");
  const [batches, setBatches] = useState([]);
  const [count, setCount] = useState(0);
  const [previous, setPrevious] = useState(null);
  const [next, setNext] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [batchName, setBatchName] = useState("Batches");
  const [tempBatchName, setTempBatchName] = useState("");
  const [showDropdown, setShowDropdown] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [currentBatch, setCurrentBatch] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(null);

  const fetchProgrammeDetails = async () => {
    if (!currentProgramme || currentProgramme.programme_code !== programmeId) {
      try {
        setIsLoading(true);
        const response = await GET(
          `${EntityConstants.GET_PROGRAMME_DETAILS}/${programmeId}`
        );
        const data = await response.json();
        if (data.error) {
          Toast({
            type: "error",
            title: "Error",
            message: data.error,
          });
          navigate("/issuer/workspace");
        } else {
          setProgramme(data.data);
        }
      } catch (error) {
        console.error("Error fetching programme:", error);
        Toast({
          type: "error",
          title: "Error",
          message: "Failed to fetch programme details",
        });
        navigate("/issuer/workspace");
      } finally {
        setIsLoading(false);
      }
    }
  };

  const fetchBatches = (limit, offset, search = "") => {
    setIsLoading(true);
    const endpoint = search
      ? `${EntityConstants.GET_BATCH_DETAILS}?programme_code=${programmeId}&limit=${limit}&offset=${offset}&search=${search}`
      : `${EntityConstants.GET_BATCH_DETAILS}?programme_code=${programmeId}&limit=${limit}&offset=${offset}`;

    GET(endpoint)
      .then((response) => response.json())
      .then((data) => {
        if (data.error) {
          Toast({
            type: "error",
            title: "Error",
            message: data.error,
          });
          navigate("/issuer/workspace");
        } else {
          const activeBatches = data.data.results.filter(
            (batch) => batch.is_active
          );
          setBatches(activeBatches);
          setCount(data.data.count);
          setPrevious(data.data.previous);
          setNext(data.data.next);
          authContext.BatchOffsetHandler(offset);
        }
      })
      .catch((error) => {
        console.error(error);
        Toast({
          type: "error",
          title: "Error",
          message: "Failed to fetch batches",
        });
        navigate("/issuer/workspace");
      })
      .finally(() => {
        setIsLoading(false);
        setIsSearching(false);
      });
  };

  const performSearchAPI = async (searchTerm, signal) => {
    try {
      const endpoint = `${EntityConstants.GET_BATCH_DETAILS}?programme_code=${programmeId}&limit=6&offset=0&search=${searchTerm}`;
      const response = await GET(endpoint, { signal });
      const data = await response.json();

      if (data.error) {
        Toast({
          type: "error",
          title: "Error",
          message: data.error,
        });
        setBatches([]);
      } else {
        const activeBatches = data.data.results.filter(
          (batch) => batch.is_active
        );
        setBatches(activeBatches);
        setCount(data.data.count);
        setPrevious(data.data.previous);
        setNext(data.data.next);
        authContext.BatchOffsetHandler(0);
      }
    } catch (error) {
      if (error.name !== "AbortError") {
        console.error(error);
        Toast({
          type: "error",
          title: "Error",
          message: "Failed to fetch batches",
        });
        setBatches([]);
      }
    }
  };

  const { handleSearch } = useSearchDebounce(performSearchAPI, 500);

  const fetchBatchName = () => {
    GET(EntityConstants.GET_BATCH_NAME)
      .then((response) => response.json())
      .then((data) => {
        if (data.batch_display_name) {
          setBatchName(data.batch_display_name);
        }
      })
      .catch((error) => {
        console.error("Error fetching batch name:", error);
      });
  };

  const handleBatchCreated = () => {
    fetchBatches(6, authContext.batchOffset || 0);
  };

  const handleEditClick = () => {
    setTempBatchName(batchName);
    setIsEditing(true);
  };

  const handleNameChange = (e) => {
    setTempBatchName(e.target.value);
  };

  const deleteBatch = async (batchCode) => {
    try {
      const response = await DEL(
        `${EntityConstants.GET_BATCH_DETAILS}?batch_id=${batchCode}`
      );
      const data = await response.json();

      if (response.ok) {
        Toast({
          type: "success",
          title: "Success",
          message: "Batch deleted successfully",
        });

        const currentOffset = authContext.batchOffset || 0;
        const pageSize = 6;

        if (batches.length === 1 && currentOffset > 0) {
          const newOffset = Math.max(0, currentOffset - pageSize);
          fetchBatches(pageSize, newOffset, searchTerm);
        } else {
          fetchBatches(pageSize, currentOffset, searchTerm);
        }
      } else {
        Toast({
          type: "error",
          title: "Error",
          message: data.error || "Failed to delete batch",
        });
      }
    } catch (error) {
      console.error("Failed to delete batch", error);
      Toast({
        type: "error",
        title: "Error",
        message: "An error occurred while deleting the batch.",
      });
    }
  };

  const handleDeleteBatch = (batch) => {
    setCurrentBatch(batch);
    setIsDeleteModalOpen(batch.batch_code);
    setShowDropdown(null);
  };

  const confirmDeleteBatch = () => {
    if (currentBatch) {
      deleteBatch(currentBatch.batch_code);
      setIsDeleteModalOpen(null);
      setCurrentBatch(null);
    }
  };

  const handleNameSubmit = () => {
    if (tempBatchName.trim() === "") {
      Toast({
        type: "error",
        title: "Error",
        message: "Batch name cannot be empty",
      });
      return;
    }

    POST(EntityConstants.GET_BATCH_NAME, { batch_display_name: tempBatchName })
      .then((response) => response.json())
      .then((data) => {
        if (data.error) {
          Toast({
            type: "error",
            title: "Error",
            message: data.error,
          });
        } else {
          setBatchName(tempBatchName);
          setIsEditing(false);
          fetchBatchName();
          Toast({
            type: "success",
            title: "Success",
            message: "Batch name updated successfully",
          });
        }
      })
      .catch((error) => {
        console.error(error);
        Toast({
          type: "error",
          title: "Error",
          message: "Failed to update batch name",
        });
      });
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleNameSubmit();
    } else if (e.key === "Escape") {
      setIsEditing(false);
    }
  };

  const handleEditBatch = (batch) => {
    setCurrentBatch(batch);
    setEditModalOpen(true);
    setShowDropdown(null);
  };

  const handleBatchUpdate = (updatedName, updatedDescription) => {
    const payload = {
      batch_code: currentBatch.batch_code,
      batch_name: updatedName,
      batch_description: updatedDescription,
    };

    PUT(EntityConstants.GET_BATCH_DETAILS, payload)
      .then((response) => response.json())
      .then((data) => {
        if (data.error) {
          Toast({
            type: "error",
            title: "Error",
            message: data.error,
          });
        } else {
          Toast({
            type: "success",
            title: "Success",
            message: "Batch updated successfully",
          });
          fetchBatches(6, authContext.batchOffset || 0);
        }
      })
      .catch((error) => {
        console.error(error);
        Toast({
          type: "error",
          title: "Error",
          message: "Failed to update batch",
        });
      });
  };

  const handleBatchLinkClick = (batch) => {
    setBatchInStore(batch);
    navigate(`/issuer/workspace/batches/credentials`);
  };

  const handleSearchInputChange = (e) => {
    const value = e.target.value ? e.target.value : "";
    setSearchTerm(value);
    authContext.BatchOffsetHandler(0);

    if (value.length > 0) {
      setIsSearching(true);
      handleSearch(value, {
        onStart: () => setIsSearching(true),
        onComplete: () => setIsSearching(false),
        onError: () => {
          setBatches([]);
          setIsSearching(false);
        },
        minChars: 1,
      });
    } else {
      setIsSearching(false);
      fetchBatches(6, 0);
    }
  };

  useEffect(() => {
    fetchProgrammeDetails();
  }, [programmeId]);

  useEffect(() => {
    if (!searchTerm) {
      fetchBatches(6, authContext.batchOffset || 0);
    }
  }, [authContext.batchOffset, programmeId]);

  useEffect(() => {
    if (authContext.batchOffset !== null) {
      localStorage.setItem("batchOffset", authContext.batchOffset);
    }
  }, [authContext.batchOffset]);

  useEffect(() => {
    fetchBatchName();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(null);
      }
    };

    if (showDropdown !== null) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showDropdown]);

  return (
    <div className="w-full mt-20 bg-cfSecondary min-h-screen">
      <div className="">
        <div
          className={`w-full bg-cfSearchContainer flex justify-center py-[0.7rem] px-4`}
        >
          <div
            className={`flex flex-col sm:flex-row items-start sm:items-center justify-between w-full`}
          >
            <div className="">
              <div className="flex items-center gap-2 text-cfHeadingText font-semibold text-lg self-start my-auto">
                <Link
                  to={`/issuer/workspace`}
                  className="flex items-center gap-2"
                  style={{ textDecoration: "none" }}
                >
                  <button className="inline-flex items-center justify-center transition-colors focus-visible:outline-none border-input hover:bg-accent hover:text-accent-foreground">
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                </Link>
                <div className="flex items-center gap-2">
                  {isEditing ? (
                    <input
                      type="text"
                      value={tempBatchName}
                      onChange={handleNameChange}
                      onKeyDown={handleKeyDown}
                      className="border focus:outline-none rounded px-2 py-1 text-lg font-medium"
                    />
                  ) : (
                    <>
                      <SquarePen
                        className="w-5 h-5 cursor-pointer text-cfSignUpText hover:text-cfCategoryCardHeadingText"
                        onClick={handleEditClick}
                      />
                      <h2 className="text-lg font-medium">{batchName}</h2>
                    </>
                  )}
                </div>
                <span className="text-sm text-cfViewAllTagText font-medium mt-[2px]">
                  ({count})
                </span>
              </div>
            </div>
            {isModalOpen && (
              <CreateBatchModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onBatchCreated={handleBatchCreated}
                programmeCode={programmeId}
              />
            )}
            <div className="flex flex-col sm:flex-row items-start sm:mt-0 gap-2 sm:gap-4 w-full sm:w-auto">
              <button
                onClick={() => setIsModalOpen(true)}
                className="h-[2.75rem] min-w-[10rem] px-2 gap-2 bg-cfButton text-cfSecondary rounded-[8px] flex items-center justify-center hover:bg-cfPrimaryDark transition duration-200 border border-cfCategoryCardHeadingText"
              >
                <img src={PlusCircle} className="h-4 w-4" alt="" />
                <span className="font-medium">Create {batchName}</span>
              </button>
              <div className="bg-cfSecondary p-2 rounded-lg w-full sm:w-[25rem]">
                <input
                  type="text"
                  name="search"
                  id="search"
                  placeholder={`Search for ${batchName}...`}
                  className="outline-none border-none p-0 placeholder:text-sm w-full"
                  style={{ fontSize: "0.875rem", lineHeight: "1.25rem" }}
                  onChange={handleSearchInputChange}
                  value={searchTerm}
                />
              </div>
            </div>
          </div>
        </div>

        {isLoading || isSearching ? (
          <div className="col-span-full w-full flex justify-center items-center py-4">
            <CfLoader />
          </div>
        ) : batches.length > 0 ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 px-4 mt-4">
              {batches
                .filter((batch) => batch.is_active)
                .map((batch) => (
                  <div
                    key={batch.batch_code}
                    onClick={() => handleBatchLinkClick(batch)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleBatchLinkClick(batch);
                      }
                    }}
                    role="button"
                    tabIndex={0}
                    className="border rounded-xl overflow-hidden hover:shadow-xl transition-all duration-300 cursor-pointer bg-cfSecondary h-full group"
                  >
                    <div className="p-6 flex flex-col h-[16rem] justify-between">
                      <div className="flex justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <div
                              className={`w-3 h-3 rounded-full ${
                                batch.is_active
                                  ? "bg-cfIssuedCredentialTemplateCardStatusDoneBG"
                                  : "bg-cfSideBarDisabledText"
                              }`}
                            ></div>
                            <span
                              className={`text-xs font-medium ${
                                batch.is_active
                                  ? "text-cfIssuedCredentialTemplateCardStatusDoneBG"
                                  : "text-cfViewAllTagText"
                              }`}
                            >
                              {batch.is_active ? "Active" : "Inactive"}
                            </span>
                          </div>
                          <div className="flex flex-col justify-between h-full">
                            <div className="flex justify-between items-start">
                              <h3 className="font-semibold text-cfCategoryCardHeadingText text-xl group-hover:text-cfPrimaryButton transition-colors line-clamp-2">
                                {batch.batch_name}
                              </h3>
                              <div className="relative">
                                <MoreVertical
                                  className="h-[1.1rem] w-[1.1rem] cursor-pointer hover:text-cfPrimaryButton transition-all duration-200"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setShowDropdown(
                                      showDropdown === batch.batch_code
                                        ? null
                                        : batch.batch_code
                                    );
                                  }}
                                />
                                {showDropdown === batch.batch_code && (
                                  <div
                                    ref={dropdownRef}
                                    className="absolute right-0 mt-2 w-28 bg-cfSecondary rounded-md shadow-lg z-10 border"
                                  >
                                    <div>
                                      <button
                                        className="block w-full text-left px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 focus:outline-none"
                                        onClick={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          handleEditBatch(batch);
                                        }}
                                      >
                                        Edit
                                      </button>
                                      <button
                                        className="block w-full text-left px-4 py-2 text-sm font-medium text-red-600 hover:bg-gray-100 focus:outline-none"
                                        onClick={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          handleDeleteBatch(batch);
                                        }}
                                      >
                                        Delete
                                      </button>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                            <div className="text-sm text-cfViewAllTagText line-clamp-1">
                              {batch.batch_description}
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="flex justify-between items-center border-t pt-4">
                        <div className="flex items-center gap-2">
                          <div className="bg-indigo-50 p-2 rounded-full">
                            <FileText className="h-5 w-5 text-cfIssuedScreenTabButtonSelectedText" />
                          </div>
                          <div>
                            <span className="block text-lg font-bold text-cfTextBreadcrumbActive">
                              {batch.number_of_templates}
                            </span>
                            <span className="block text-xs text-cfDropdownOptionsText">
                              Certificates
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
            </div>

            <div className="flex items-center justify-between mt-6 px-4 w-full">
              <button
                onClick={() =>
                  authContext.BatchOffsetHandler(authContext.batchOffset - 6)
                }
                className="flex items-center justify-center px-5 py-2 w-36 text-sm active:text-cfCardTagText capitalize transition-colors duration-200 bg-cfSecondary border rounded-md gap-x-2 hover:bg-gray-100 disabled:text-gray-400"
                disabled={previous === null}
              >
                <span className="flex items-center justify-center gap-2">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="1.5"
                    stroke="currentColor"
                    className="w-5 h-5 rtl:-scale-x-100"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M6.75 15.75L3 12m0 0l3.75-3.75M3 12h18"
                    />
                  </svg>
                  <span>previous</span>
                </span>
              </button>

              <button
                onClick={() =>
                  authContext.BatchOffsetHandler(authContext.batchOffset + 6)
                }
                className="flex items-center justify-center px-5 py-2 w-36 text-sm active:text-cfCardTagText capitalize transition-colors duration-200 bg-cfSecondary border rounded-md gap-x-2 hover:bg-gray-100 disabled:text-gray-400"
                disabled={next === null}
              >
                <span className="flex items-center justify-center gap-2">
                  <span>Next</span>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="1.5"
                    stroke="currentColor"
                    className="w-5 h-5 rtl:-scale-x-100"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M17.25 8.25L21 12m0 0l-3.75 3.75M21 12H3"
                    />
                  </svg>
                </span>
              </button>
            </div>
          </>
        ) : (
          <div className="mt-6 w-full flex justify-center">
            <p className="text-lg font-semibold text-cfSignUpText">
              No {batchName} found
            </p>
          </div>
        )}
      </div>

      {editModalOpen && currentBatch && (
        <CreateBatchModal
          isOpen={editModalOpen}
          onClose={() => setEditModalOpen(false)}
          onBatchCreated={() => {
            fetchBatches(6, authContext.batchOffset || 0);
            setEditModalOpen(false);
          }}
          programmeCode={programmeId}
          isEditMode={true}
          initialName={currentBatch.batch_name}
          initialDescription={currentBatch.batch_description}
          onUpdate={handleBatchUpdate}
        />
      )}
      <DraftsRevokeModal
        isOpen={isDeleteModalOpen !== null}
        onClose={() => {
          setIsDeleteModalOpen(null);
          setCurrentBatch(null);
        }}
        onConfirm={confirmDeleteBatch}
        title="Delete Batch"
        description={`Are you sure you want to delete the batch "${currentBatch?.batch_name}"? This action cannot be undone.`}
      />
    </div>
  );
}
